import { Types } from 'mongoose';
import { z } from 'zod';

/**
 * Folder IDs for categorizing knowledge sources
 */
export enum DataSourcesFolderIds {
  Priority = 'data-sources.folder.priority',
  Core = 'data-sources.folder.core',
  Train = 'data-sources.folder.train',
  Temporary = 'data-sources.folder.temp',
  Shared = 'data-sources.folder.shared',
}

export const DATA_SOURCES_FOLDER_DEFINITIONS: Record<
  DataSourcesFolderIds,
  {
    name: string;
    description: string;
    metadata: {
      icon: string;
      borderColor: string;
      backgroundColor: string;
      iconColor: string;
      canCreateSubfolders?: boolean;
      sortOrder?: number;
    };
  }
> = {
  [DataSourcesFolderIds.Priority]: {
    name: `${DataSourcesFolderIds.Priority}.title`,
    description: `${DataSourcesFolderIds.Priority}.desc`,
    metadata: {
      icon: 'star',
      borderColor: '#FED7AA',
      backgroundColor: '#FFF7ED',
      iconColor: '#EA580C',
      canCreateSubfolders: true,
      sortOrder: 0,
    },
  },
  [DataSourcesFolderIds.Core]: {
    name: `${DataSourcesFolderIds.Core}.title`,
    description: `${DataSourcesFolderIds.Core}.desc`,
    metadata: {
      icon: 'storage',
      borderColor: '#BFDBFE',
      backgroundColor: '#EFF6FF',
      iconColor: '#2563EB',
      canCreateSubfolders: true,
      sortOrder: 1,
    },
  },
  [DataSourcesFolderIds.Train]: {
    name: `${DataSourcesFolderIds.Train}.title`,
    description: `${DataSourcesFolderIds.Train}.desc`,
    metadata: {
      icon: 'lightbulb',
      borderColor: '#E9D5FF',
      backgroundColor: '#FAF5FF',
      iconColor: '#9333EA',
      canCreateSubfolders: false,
      sortOrder: 2,
    },
  },
  [DataSourcesFolderIds.Temporary]: {
    name: `${DataSourcesFolderIds.Temporary}.title`,
    description: `${DataSourcesFolderIds.Temporary}.desc`,
    metadata: {
      icon: 'timer',
      borderColor: '#FDE68A',
      backgroundColor: '#FFFBEB',
      iconColor: '#D97706',
      canCreateSubfolders: true,
      sortOrder: 3,
    },
  },
  [DataSourcesFolderIds.Shared]: {
    name: `${DataSourcesFolderIds.Shared}.title`,
    description: `${DataSourcesFolderIds.Shared}.desc`,
    metadata: {
      icon: 'folder_shared',
      borderColor: '#99F6E4',
      backgroundColor: '#F0FDFA',
      iconColor: '#0D9488',
      canCreateSubfolders: false,
      sortOrder: 4,
    },
  },
};

export const DataSourceFolderMetadataSchema = z
  .object({
    icon: z.string().optional(),
    backgroundColor: z.string().optional(),
    borderColor: z.string().optional(),
    iconColor: z.string().optional(),
    canCreateSubfolders: z.boolean().optional(),
    sortOrder: z.number().optional(),
  })
  .optional();

/**
 * Schema for data source folder data
 */
export const DataSourceFolderSchema = z.object({
  _id: Types.ObjectId,
  name: z.string().max(50),
  description: z.string().max(200).optional(),
  companyId: z.string().optional(),
  global: z.boolean().optional(),
  metadata: DataSourceFolderMetadataSchema,
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export type DataSourceFolderDTO = z.infer<typeof DataSourceFolderSchema>;

/**
 * Schema for nested subfolder (within folder response)
 */
export const DataSourceSubfolderNestedSchema = z.object({
  _id: Types.ObjectId,
  parentFolderId: Types.ObjectId,
  name: z.string().max(50),
  description: z.string().max(200).optional(),
  companyId: z.string(),
  documentCount: z.number().default(0),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

/**
 * Schema for folder with nested subfolders and counts
 */
export const DataSourceFolderWithSubfoldersSchema = z.object({
  _id: Types.ObjectId,
  name: z.string().max(50),
  description: z.string().max(200).optional(),
  companyId: z.string().optional(),
  global: z.boolean().optional(),
  documentCount: z.number().default(0),
  hasExpiringSoon: z.boolean().default(false),
  subfolders: z.array(DataSourceSubfolderNestedSchema).default([]),
  metadata: DataSourceFolderMetadataSchema,
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export type DataSourceSubfolderNestedDTO = z.infer<
  typeof DataSourceSubfolderNestedSchema
>;
export type DataSourceFolderWithSubfoldersDTO = z.infer<
  typeof DataSourceFolderWithSubfoldersSchema
>;
